import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronDown, ChevronRight, Search } from "lucide-react";

import { useTranslation } from "../i18n";
import { ADMIN_DESTINATIONS, AdminDest, filterAdminDestinations } from "./admin-nav";
import {
    ADMIN_LIVE_SECTIONS, ADMIN_LIVE_SECTION_ORDER, AdminSectionId,
    AdminSearchResult, MIN_LIVE_QUERY_LENGTH, clearLiveSearchCache,
} from "./admin-search-sections";

interface AdminSearchProps {
    /** Extra class on the wrapper — used by the header to switch into a
     *  dark-on-light variant (`admin-search--dark`). */
    className?: string;
}

// Search input that filters the static admin destinations index AND, when a
// non-Pages section is selected, fetches live results from that section's
// existing API. Used by AdminPageHeader (in-page on /admin/settings) and by
// the top header (every /admin/* route for admins) so the same dropdown
// powers both surfaces — only the styling differs.
//
// Keyboard: ↑/↓ moves selection, Enter goes to the highlighted result (or
// jumps to the section's page with `?search=Q` when there are no results),
// Esc clears.
const DEBOUNCE_MS = 250;

const AdminSearch = ({ className }: AdminSearchProps = {}) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [query, setQuery] = useState("");
    const [section, setSection] = useState<AdminSectionId>("pages");
    const [open, setOpen] = useState(false);
    const [highlighted, setHighlighted] = useState(0);
    const [sectionMenuOpen, setSectionMenuOpen] = useState(false);

    const wrapperRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    // Debounced query is what feeds the live fetchers. The Pages section
    // doesn't need debounce (synchronous filter) but reading the same
    // debounced value keeps the highlight reset behaviour consistent.
    const [debouncedQuery, setDebouncedQuery] = useState("");
    useEffect(() => {
        const id = setTimeout(() => setDebouncedQuery(query), DEBOUNCE_MS);
        return () => clearTimeout(id);
    }, [query]);

    // Live fetch state for non-Pages sections. We keep the request id so
    // a stale response can't overwrite a fresher one (out-of-order
    // resolution when the user types fast).
    const [liveResults, setLiveResults] = useState<AdminSearchResult[] | null>(null);
    const [liveLoading, setLiveLoading] = useState(false);
    const [liveError, setLiveError] = useState<string | null>(null);
    const requestIdRef = useRef(0);

    const isPagesSection = section === "pages";
    const trimmedQuery = debouncedQuery.trim();

    // Pull the current warehouse out of localStorage at hook time. Updates
    // to user.warehouse trigger a full reload (see AdminWarehouseSwitcher),
    // so we don't need to react to it within this component.
    const warehouse = useMemo(() => {
        try {
            const raw = window.localStorage.getItem("user");
            if (!raw) return "";
            return (JSON.parse(raw).warehouse as string) || "";
        } catch { return ""; }
    }, []);

    // === Pages: synchronous index filter ====================================
    const pagesMatches = useMemo<AdminDest[]>(
        () => (isPagesSection ? filterAdminDestinations(trimmedQuery, t) : []),
        [isPagesSection, trimmedQuery, t],
    );

    // Section may override the global min-length (e.g. Users drops to
    // 1 because typing a single letter already narrows the set
    // usefully). Pages section never uses this threshold.
    const sectionMinLength = isPagesSection
        ? MIN_LIVE_QUERY_LENGTH
        : (ADMIN_LIVE_SECTIONS[section as Exclude<AdminSectionId, "pages">]?.minQueryLength ?? MIN_LIVE_QUERY_LENGTH);

    // === Live sections: debounced fetch =====================================
    useEffect(() => {
        if (isPagesSection) {
            setLiveResults(null);
            setLiveLoading(false);
            setLiveError(null);
            return;
        }
        if (trimmedQuery.length < sectionMinLength) {
            setLiveResults(null);
            setLiveLoading(false);
            setLiveError(null);
            return;
        }
        const cfg = ADMIN_LIVE_SECTIONS[section];
        if (!cfg) return;
        const myId = ++requestIdRef.current;
        setLiveLoading(true);
        setLiveError(null);
        cfg.fetch(trimmedQuery, warehouse)
            .then(rows => {
                if (requestIdRef.current !== myId) return;
                setLiveResults(rows);
                setLiveLoading(false);
            })
            .catch(err => {
                if (requestIdRef.current !== myId) return;
                setLiveError(err?.message || "error");
                setLiveLoading(false);
                setLiveResults([]);
            });
    }, [isPagesSection, section, trimmedQuery, warehouse, sectionMinLength]);

    // Close on outside click — mousedown rather than click so the focus
    // state is clean before any other handler fires.
    useEffect(() => {
        const onDown = (e: MouseEvent) => {
            if (!wrapperRef.current?.contains(e.target as Node)) {
                setOpen(false);
                setSectionMenuOpen(false);
            }
        };
        document.addEventListener("mousedown", onDown);
        return () => document.removeEventListener("mousedown", onDown);
    }, []);

    // Reset highlight whenever the result set could have changed.
    useEffect(() => { setHighlighted(0); }, [debouncedQuery, section]);

    // ---- Navigation helpers -------------------------------------------------
    const goToDest = (dest: AdminDest) => {
        setQuery("");
        setOpen(false);
        inputRef.current?.blur();
        navigate(dest.to);
    };

    const goToSectionPage = (q: string) => {
        const cfg = ADMIN_LIVE_SECTIONS[section as Exclude<AdminSectionId, "pages">];
        if (!cfg) return;
        const target = q ? `${cfg.route}?search=${encodeURIComponent(q)}` : cfg.route;
        setQuery("");
        setOpen(false);
        inputRef.current?.blur();
        navigate(target);
    };

    // ---- What's currently shown in the dropdown -----------------------------
    const showDropdown = open && trimmedQuery.length > 0;

    const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Escape") {
            setQuery("");
            setOpen(false);
            inputRef.current?.blur();
            return;
        }
        if (!showDropdown) return;

        if (isPagesSection) {
            if (pagesMatches.length === 0) return;
            if (e.key === "ArrowDown") { e.preventDefault(); setHighlighted(h => Math.min(h + 1, pagesMatches.length - 1)); }
            else if (e.key === "ArrowUp") { e.preventDefault(); setHighlighted(h => Math.max(h - 1, 0)); }
            else if (e.key === "Enter") { e.preventDefault(); goToDest(pagesMatches[highlighted]); }
            return;
        }

        // Live section. Enter prefers the highlighted row's identifying
        // value (so "joh" + Enter on a highlighted "John Smith" lands on
        // ?search=John%20Smith, not ?search=joh) and falls back to the
        // typed query when no preview rows are loaded yet.
        if (e.key === "Enter") {
            e.preventDefault();
            const target = liveResults && liveResults[highlighted];
            goToSectionPage(target?.searchTerm || trimmedQuery);
            return;
        }
        if (!liveResults || liveResults.length === 0) return;
        if (e.key === "ArrowDown") { e.preventDefault(); setHighlighted(h => Math.min(h + 1, liveResults.length - 1)); }
        else if (e.key === "ArrowUp") { e.preventDefault(); setHighlighted(h => Math.max(h - 1, 0)); }
    };

    // ---- Section pill dropdown ---------------------------------------------
    const sections = ADMIN_LIVE_SECTION_ORDER;
    const currentSectionLabel = isPagesSection
        ? t("admin_search_section_pages")
        : t(ADMIN_LIVE_SECTIONS[section as Exclude<AdminSectionId, "pages">].labelKey);

    return (
        <div className={`admin-search${className ? " " + className : ""}`} ref={wrapperRef}>
            <div className="admin-search-input-wrap">
                <button
                    type="button"
                    className="admin-search-section-button"
                    onClick={() => setSectionMenuOpen(o => !o)}
                    aria-haspopup="listbox"
                    aria-expanded={sectionMenuOpen}
                >
                    <span className="admin-search-section-label">{currentSectionLabel}</span>
                    <ChevronDown size={14} strokeWidth={1.8} />
                </button>
                {sectionMenuOpen && (
                    <div className="admin-search-section-menu" role="listbox">
                        {sections.map(id => {
                            const isPages = id === "pages";
                            const label = isPages
                                ? t("admin_search_section_pages")
                                : t(ADMIN_LIVE_SECTIONS[id as Exclude<AdminSectionId, "pages">].labelKey);
                            return (
                                <button
                                    key={id}
                                    type="button"
                                    role="option"
                                    aria-selected={id === section}
                                    className={`admin-search-section-item${id === section ? " admin-search-section-item--active" : ""}`}
                                    onClick={() => {
                                        setSection(id);
                                        setSectionMenuOpen(false);
                                        inputRef.current?.focus();
                                    }}
                                >
                                    {label}
                                </button>
                            );
                        })}
                    </div>
                )}
                <div className="admin-search-field">
                    <Search size={16} className="admin-search-icon" />
                    <input
                        ref={inputRef}
                        className="admin-search-input"
                        type="search"
                        inputMode="search"
                        placeholder={
                            isPagesSection
                                ? t("search_admin_placeholder")
                                : t("search_in_placeholder", { section: currentSectionLabel })
                        }
                        value={query}
                        onChange={e => { setQuery(e.target.value); setOpen(true); }}
                        onFocus={() => {
                            // Flush per-section caches so a fresh search
                            // session always sees the latest server data —
                            // the user pointed out that recently-edited
                            // rows weren't surfacing without this.
                            clearLiveSearchCache();
                            setOpen(true);
                        }}
                        onKeyDown={onKeyDown}
                        aria-label={t("search_admin_placeholder")}
                    />
                </div>
            </div>

            {showDropdown && (
                <div className="admin-search-dropdown" role="listbox">
                    {isPagesSection ? renderPages() : renderLive()}
                </div>
            )}
        </div>
    );

    // -- Sub-renderers ---------------------------------------------------------

    function renderPages() {
        if (pagesMatches.length === 0) {
            return <div className="admin-search-empty">{t("search_no_results")}</div>;
        }
        return pagesMatches.map((dest, i) => (
            <button
                key={dest.to}
                type="button"
                className={`admin-search-item ${i === highlighted ? "admin-search-item--active" : ""}`}
                onMouseEnter={() => setHighlighted(i)}
                onMouseDown={e => { e.preventDefault(); goToDest(dest); }}
                role="option"
                aria-selected={i === highlighted}
            >
                <div className="admin-search-item-label">{t(dest.labelKey)}</div>
                <div className="admin-search-item-crumbs">
                    {dest.crumbKeys.map((k, j) => (
                        <span key={k} className="admin-search-item-crumb">
                            {j > 0 && <ChevronRight size={12} className="admin-search-item-sep" />}
                            {t(k)}
                        </span>
                    ))}
                </div>
            </button>
        ));
    }

    function renderLive() {
        if (trimmedQuery.length < sectionMinLength) {
            return (
                <div className="admin-search-empty">
                    {t("search_min_chars", { n: String(sectionMinLength) })}
                </div>
            );
        }
        if (liveLoading && (!liveResults || liveResults.length === 0)) {
            return <div className="admin-search-empty">{t("loading")}…</div>;
        }
        if (liveError) {
            return <div className="admin-search-empty">{t("error")}</div>;
        }
        if (!liveResults || liveResults.length === 0) {
            return (
                <div className="admin-search-empty">
                    {t("search_no_results")}
                </div>
            );
        }
        return (
            <>
                {liveResults.map((r, i) => (
                    <button
                        key={r.id}
                        type="button"
                        className={`admin-search-item ${i === highlighted ? "admin-search-item--active" : ""}`}
                        onMouseEnter={() => setHighlighted(i)}
                        onMouseDown={e => { e.preventDefault(); goToSectionPage(r.searchTerm || trimmedQuery); }}
                        role="option"
                        aria-selected={i === highlighted}
                    >
                        <div className="admin-search-item-label">{r.label}</div>
                        {r.sublabel && (
                            <div className="admin-search-item-crumbs">
                                <span className="admin-search-item-crumb">{r.sublabel}</span>
                            </div>
                        )}
                    </button>
                ))}
                <button
                    type="button"
                    className="admin-search-item admin-search-item--all"
                    onMouseDown={e => { e.preventDefault(); goToSectionPage(trimmedQuery); }}
                >
                    <div className="admin-search-item-label">
                        {t("search_view_all_in", { section: currentSectionLabel })}
                    </div>
                </button>
            </>
        );
    }
};

// Re-exported so callers can sanity-check the index size without importing
// from admin-nav directly (e.g. in tests).
export const ADMIN_SEARCH_INDEX_SIZE = ADMIN_DESTINATIONS.length;

export default AdminSearch;
